<template>
  <div id="app" class="app-layout" :class="{ 'with-sidebar': showSidebar }">
    <TheHeader v-if="showSidebar" />
    <div class="main-content">
      <router-view :templates="templates" @create-template="addTemplate" @update-template="updateTemplate" @cancel-edit="cancelEdit" />
    </div>
  </div>
</template>

<script>
import TheHeader from "@/components/TheHeader.vue";
import { inject, computed } from "vue";
import { useRoute } from "vue-router";

export default {
  name: "App",
  components: {
    TheHeader,
  },
  setup() {
    const route = useRoute();
    const { profile } = inject('user');
    
    const showSidebar = computed(() => {
      // Don't show sidebar on login, signup, or if explicitly hidden
      if (route.name === 'login' || route.name === 'signup' || route.meta.navbar === false) {
        return false;
      }
      return true;
    });

    return { showSidebar };
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

body {
  margin: 0;
  padding: 0;
  background-color: #F8F9FC;
  font-family: 'Outfit', sans-serif;
  color: #11142D;
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  color: #11142D;
}

/* App Layout */
.app-layout {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  width: 100%;
  overflow-x: hidden;
  padding: 2rem;
}

.app-layout.with-sidebar .main-content {
  margin-left: 260px; /* Width of the sidebar */
  background-color: #F8F9FC;
}

@media (max-width: 991px) {
  .app-layout.with-sidebar .main-content {
    margin-left: 0;
    padding-top: 60px; /* Mobile header space */
  }
}

/* GLOBAL COMPONENT OVERRIDES TO MATCH TIME CHANGE DESIGN SYSTEM */

/* Cards */
.card {
  background: #ffffff !important;
  border: 1px solid #E2E2EA !important;
  border-radius: 16px !important;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03) !important;
  overflow: hidden;
}

.card-header {
  background-color: #ffffff !important;
  border-bottom: 1px solid #E2E2EA !important;
  font-weight: 700;
  padding: 1.25rem 1.5rem !important;
}

.card-body {
  padding: 1.5rem !important;
}

/* Buttons */
.btn {
  border-radius: 50rem !important;
  font-weight: 600 !important;
  padding: 0.6rem 1.5rem !important;
  transition: transform 0.2s, box-shadow 0.2s !important;
}

.btn-primary {
  background: linear-gradient(135deg, #6C5DD3, #8a7df0) !important;
  border: none !important;
  color: white !important;
  box-shadow: 0 4px 14px 0 rgba(108, 93, 211, 0.3) !important;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(108, 93, 211, 0.4) !important;
}

.btn-success {
  background: #20C997 !important;
  border: none !important;
  color: white !important;
}
.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(32, 201, 151, 0.4) !important;
}

.btn-danger {
  background: #FF754C !important;
  border: none !important;
  color: white !important;
}

/* Forms */
.form-control, .form-select, .custom-select {
  background-color: #F8F9FC !important;
  border: 1px solid #E2E2EA !important;
  border-radius: 12px !important;
  padding: 0.8rem 1.2rem !important;
  font-size: 1rem !important;
  color: #11142D !important;
  transition: all 0.2s !important;
  height: auto !important;
  min-height: 48px !important;
}

.b-form-datepicker {
  height: auto !important;
  min-height: 48px !important;
  padding: 0 !important;
}

.b-form-datepicker .btn {
  padding: 0.8rem 1rem !important;
}

.form-control:focus, .form-select:focus, .custom-select:focus {
  border-color: #6C5DD3 !important;
  box-shadow: 0 0 0 0.25rem rgba(108, 93, 211, 0.15) !important;
  background-color: #ffffff !important;
}

label {
  font-weight: 600;
  color: #B2B2BF;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

/* Tables */
.table {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  margin-bottom: 2rem;
}

.table thead th {
  background-color: #F8F9FC !important;
  border-bottom: 2px solid #E2E2EA !important;
  border-top: none !important;
  color: #B2B2BF;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  padding: 1.25rem 1rem !important;
}

.table tbody td {
  padding: 1.25rem 1rem !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #E2E2EA !important;
  color: #11142D;
  font-weight: 500;
}

.table-hover tbody tr:hover {
  background-color: #F0EDFF !important;
}

/* Reusable Utilities */
.glass-panel {
  background: #ffffff;
  border: 1px solid #E2E2EA;
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
}

.text-gradient {
  background: linear-gradient(135deg, #6C5DD3, #8a7df0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>
